import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getCurrentUser, signIn, signOut, signUp, confirmSignUp, type SignInInput, type SignUpInput } from 'aws-amplify/auth';

interface User {
  userId: string;
  username: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (input: SignInInput) => Promise<void>;
  logout: () => Promise<void>;
  register: (input: SignUpInput) => Promise<{ isSignUpComplete: boolean; nextStep: unknown }>;
  confirmRegistration: (username: string, code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId,
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const login = async (input: SignInInput) => {
    await signIn(input);
    await checkUser();
  };

  const logout = async () => {
    await signOut();
    setUser(null);
  };

  const register = async (input: SignUpInput) => {
    const result = await signUp(input);
    return { isSignUpComplete: result.isSignUpComplete, nextStep: result.nextStep };
  };

  const confirmRegistration = async (username: string, code: string) => {
    await confirmSignUp({ username, confirmationCode: code });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        register,
        confirmRegistration,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
